import { storageService } from './storage.js';
import { syncService } from './sync.js';
import { dom } from './dom.js';
import * as state from './state.js';
import { showMessage, showConfirm, formatCurrency, applyCurrency, escapeHTML, setupClickOutside, exportToCSV } from './utils.js';
import flatpickr from 'flatpickr';
import { t, currentLang } from './i18n.js';
import { getFlatpickrLocale } from './locale.js';
import { syncWidgetSnapshot } from './widget.js';

const MAX_AMOUNT = 99999999; // 上限 1 億 (9,999 萬)

// 統一使用 stroke-width="1.2" 的極細現代化圖示
const categoryIcons = {
    'cat_food': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/></svg>',
    'cat_transport': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></svg>',
    'cat_shopping': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.048 18.566A2 2 0 0 0 4 21h16a2 2 0 0 0 1.952-2.434l-2-9A2 2 0 0 0 18 8H6a2 2 0 0 0-1.952 1.566z"/><path d="M8 11V6a4 4 0 0 1 8 0v5"/></svg>',
    'cat_entertainment': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="2"/><path d="M12 2v4"/><path d="m6.8 15-3.5 2"/><path d="m20.7 7-3.5 2"/><path d="M6.8 9 3.3 7"/><path d="m20.7 17-3.5-2"/><path d="m9 22 3-8 3 8"/><path d="M8 22h8"/><path d="M18 18.7a9 9 0 1 0-12 0"/></svg>',
    'cat_housing': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
    'cat_medical': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/><path d="M3.22 13H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"/></svg>',
    'cat_education': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
    'cat_general': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" /><rect width="7" height="7" x="3" y="14" rx="1" /></svg>',
    'cat_salary': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="12" x="2" y="6" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" /></svg>',
    'cat_bonus': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>',
    'cat_investment': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg>',
    'cat_revenue': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17"/><path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/><path d="m2 16 6 6"/><circle cx="16" cy="9" r="2.9"/><circle cx="6" cy="5" r="3"/></svg>',
    'cat_parttime': '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="10"/></svg>',
};

function closeAddTxModal() {
    dom.addTxModal.style.display = 'none';
    dom.addTxModal.style.paddingBottom = '0px';
    resetTxModal();
}

/**
 * 更新類型切換按鈕的 UI 與滑塊位置
 */
function updateTypeToggleUI(type) {
    const isExpense = type === 'expense';
    dom.txType.value = type;
    
    if (isExpense) {
        if (dom.txTypeSlider) {
            dom.txTypeSlider.style.transform = 'translateX(0)';
            dom.txTypeSlider.classList.remove('is-income');
        }
        dom.typeExpenseBtn.classList.add('text-white');
        dom.typeExpenseBtn.classList.remove('text-slate-500', 'dark:text-slate-400');
        dom.typeIncomeBtn.classList.add('text-slate-500', 'dark:text-slate-400');
        dom.typeIncomeBtn.classList.remove('text-white');
        
        // 更新備註提示文字
        if (dom.txDescription) dom.txDescription.placeholder = t('itemPlaceholderExpense');
    } else {
        if (dom.txTypeSlider) {
            dom.txTypeSlider.style.transform = 'translateX(100%)';
            dom.txTypeSlider.classList.add('is-income');
        }
        dom.typeIncomeBtn.classList.add('text-white');
        dom.typeIncomeBtn.classList.remove('text-slate-500', 'dark:text-slate-400');
        dom.typeExpenseBtn.classList.add('text-slate-500', 'dark:text-slate-400');
        dom.typeExpenseBtn.classList.remove('text-white');
        
        // 更新備註提示文字
        if (dom.txDescription) dom.txDescription.placeholder = t('itemPlaceholderIncome');
    }
    
    filterCategories(type);
    
    // 切換類型後，如果當前類別不匹配，則重置為「一般」
    const currentCat = dom.txCategory.value;
    const isCatValid = Array.from(dom.txCategoryOptionItems).some(opt => opt.dataset.value === currentCat && opt.dataset.type === type);
    
    if (!isCatValid) {
        dom.txCategory.value = 'cat_general';
        dom.txCategoryBtnText.textContent = t('cat_general');
        refreshCategoryOptionsUI();
    }
}

/**
 * 刷新類別選擇選單的 UI (圖示、選中狀態)
 */
function refreshCategoryOptionsUI() {
    const currentVal = dom.txCategory.value;
    dom.txCategoryOptionItems.forEach(option => {
        const val = option.dataset.value;
        const iconSvg = categoryIcons[val] || categoryIcons['cat_general'];
        
        // 確保內部結構正確：[SVG] + [SPAN(data-i18n)]
        // 我們不使用 innerHTML 覆蓋整個內容，而是精確查找 span 
        let iconWrapper = option.querySelector('.icon-wrapper');
        if (!iconWrapper) {
            iconWrapper = document.createElement('div');
            iconWrapper.className = 'icon-wrapper w-4 h-4';
            option.prepend(iconWrapper);
        }
        iconWrapper.innerHTML = iconSvg;

        // 更新選中狀態樣式
        if (val === currentVal) {
            option.classList.add('selected');
        } else {
            option.classList.remove('selected');
        }
    });
}

export async function loadTransactions() {
    try {
        const transactions = await storageService.getTransactions(state.userId, state.currentDate);
        transactions.sort((a, b) => b.date.localeCompare(a.date) || b.id - a.id);
        state.setLocalTransactions(transactions);
        updateUI(transactions);
        // 同步桌面小組件數據
        syncWidgetSnapshot();
    } catch (error) {
        console.error("Error loading transactions:", error);
    }
}

/**
 * 開啟新增交易彈窗，可預設類型與分類（支援 Deep Link 快捷記帳）
 */
export function openAddTransactionModal({ type = 'expense', category = null } = {}) {
    resetTxModal();
    dom.addTxModal.style.display = 'flex';
    dom.addTxModal.style.zIndex = '200';

    if (type) {
        updateTypeToggleUI(type);
    }

    if (category) {
        dom.txCategory.value = category;
        const matchingOpt = Array.from(dom.txCategoryOptionItems).find(opt => opt.dataset.value === category && opt.dataset.type === type);
        if (matchingOpt) {
            const span = matchingOpt.querySelector('span[data-i18n]');
            dom.txCategoryBtnText.textContent = span ? span.textContent : t(category);
        }
        refreshCategoryOptionsUI();
    }

    setTimeout(() => {
        const dp = state.flatpickrInstance;
        if (dp) {
            dp.setDate(new Date(), false);
        }

        if (dom.txAmount) {
            dom.txAmount.focus({ preventScroll: true });
            setTimeout(() => {
                if (document.activeElement === dom.txAmount) {
                    dom.txAmount.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 200);
        }
    }, 50);
}

/**
 * 獲取所有紀錄並開始導出
 */
export async function startDataExport() {
    try {
        // 獲取所有交易紀錄 (傳入 null 作為日期以獲取全部)
        const allTransactions = await storageService.getTransactions(state.userId, null);
        if (allTransactions.length === 0) {
            showMessage(t('emptyState'), 'error');
            return;
        }
        // 按日期排序 (由新到舊)
        allTransactions.sort((a, b) => b.date.localeCompare(a.date));
        await exportToCSV(allTransactions);
    } catch (error) {
        console.error("Export fetch failed:", error);
        showMessage(t('exportFailed').replace('{error}', error.message), 'error');
    }
}

function updateUI(transactions) {
    dom.transactionList.innerHTML = ''; 
    if (transactions.length === 0) {
        dom.emptyState.innerHTML = `
            <div class="flex flex-col items-center justify-center space-y-2 text-center py-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-400/80 dark:text-slate-500/80 mb-1">
                    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/>
                    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
                    <path d="M12 17.5v-11"/>
                </svg>
                <div class="flex flex-col items-center space-y-1">
                    <span class="text-sm font-semibold text-slate-700 dark:text-slate-200 tracking-wide" data-i18n="emptyState">${t('emptyState')}</span>
                    <span class="text-[12px] font-medium text-slate-500/90 dark:text-slate-400/90" data-i18n="emptyStateSub">${t('emptyStateSub')}</span>
                </div>
            </div>
        `;
        dom.transactionList.appendChild(dom.emptyState);
    } else {
        if (dom.emptyState.parentNode === dom.transactionList) {
            dom.transactionList.removeChild(dom.emptyState);
        }
    }
    
    // 計算每筆交易歷史累計餘額 (按照時間從舊到新正向累算)
    const sortedChronological = [...transactions].sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id);
    const runningBalanceMap = new Map();
    let currentAccumBalance = 0;

    sortedChronological.forEach(tx => {
        const delta = tx.type === 'income' ? tx.amount : -tx.amount;
        currentAccumBalance += delta;
        runningBalanceMap.set(tx.id, currentAccumBalance);
    });

    // 資料分組處理 ( UI 視圖仍維持最新日期在前的排版 )
    const grouped = {};
    let totalIncome = 0;
    let totalExpense = 0;

    transactions.forEach(tx => {
        if (tx.type === 'income') totalIncome += tx.amount;
        else totalExpense += tx.amount;

        if (!grouped[tx.date]) {
            grouped[tx.date] = { items: [], dayTotal: 0 };
        }
        grouped[tx.date].items.push(tx);
        // 當日小計：收入為正，支出為負
        grouped[tx.date].dayTotal += (tx.type === 'income' ? tx.amount : -tx.amount);
    });

    // 取得排序後的日期
    const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    let staggerIndex = 0; // 階梯動畫索引
    sortedDates.forEach(date => {
        renderDateHeader(date, grouped[date].dayTotal);
        grouped[date].items.forEach(tx => {
            const runningBal = runningBalanceMap.get(tx.id) || 0;
            renderTransaction(tx, staggerIndex, runningBal);
            staggerIndex++;
        });
    });

    const balance = totalIncome - totalExpense;
    applyCurrency(dom.currentBalanceEl, balance, { base: 'text-3xl', medium: 'text-2xl', small: 'text-xl' });
    
    if (balance >= 0) {
        if (dom.currentBalanceEl) {
            dom.currentBalanceEl.classList.remove('text-rose-600', 'dark:text-rose-400');
            dom.currentBalanceEl.classList.add('text-slate-950', 'dark:text-white');
        }
    }
    else {
        if (dom.currentBalanceEl) {
            dom.currentBalanceEl.classList.remove('text-slate-950', 'dark:text-white');
            dom.currentBalanceEl.classList.add('text-rose-600', 'dark:text-rose-400');
        }
    }
}

function renderDateHeader(dateStr, dayTotal) {
    const header = document.createElement('div');
    header.className = 'date-group-header flex items-center justify-between pt-6 pb-2 px-1 border-b border-white/5 mb-2';
    
    // 格式化日期顯示 (還原回 12月20日)
    const dateObj = new Date(dateStr);
    const options = { month: 'short', day: 'numeric' };
    const formattedDate = dateObj.toLocaleDateString(currentLang === 'en' ? 'en-US' : (currentLang === 'zh-CN' ? 'zh-CN' : 'zh-TW'), options);
    
    const totalColor = dayTotal >= 0 ? 'text-emerald-500/70' : 'text-rose-500/70';
    const totalPrefix = dayTotal >= 0 ? '+' : '';

    header.innerHTML = `
        <span class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">${formattedDate}</span>
        <span class="text-[9px] font-bold ${totalColor} tracking-wider">${totalPrefix}${formatCurrency(dayTotal)}</span>
    `;
    dom.transactionList.appendChild(header);
}

function renderTransaction(tx, index = 0, runningBalance = 0) {
    const isIncome = tx.type === 'income';
    const amountColor = isIncome ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400';
    const amountPrefix = isIncome ? '+' : '-';
    const item = document.createElement('div');
    
    item.className = 'glass-panel tx-item-animate flex items-center justify-between p-3.5 bg-white/10 dark:bg-slate-800/40 backdrop-blur-2xl rounded-2xl border border-white/20 dark:border-white/10 shadow-sm dark:shadow-lg cursor-pointer active:scale-[0.98] transition-all mb-2.5';
    item.style.animationDelay = `${index * 0.04}s`; 
    
    const displayTitle = tx.description ? escapeHTML(tx.description) : t(tx.category || 'cat_general');
    const iconSvg = categoryIcons[tx.category] || categoryIcons['cat_general'];

    // 判斷是否開啟「顯示每筆餘額」 (優化高對比清晰度：使用 text-slate-500 / dark:text-slate-400)
    const runningBalanceHtml = state.showRunningBalance ? `
        <div class="flex items-center justify-end space-x-1 mt-0.5 text-[10.5px] font-semibold text-slate-500 dark:text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-2.5 h-2.5 text-slate-400 dark:text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>
            <span>${formatCurrency(runningBalance)}</span>
        </div>
    ` : '';

    item.innerHTML = `
        <div class="flex items-center space-x-3 flex-1 overflow-hidden text-left">
            <div class="flex-shrink-0 w-8 h-8 rounded-xl bg-indigo-50/50 dark:bg-slate-700/60 dark:border dark:border-white/10 shadow-sm flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <div class="w-4 h-4 flex items-center justify-center">
                    ${iconSvg}
                </div>
            </div>
            <div class="flex-1 overflow-hidden">
                <p class="font-bold text-slate-900 dark:text-slate-100 truncate text-[14px] leading-tight tracking-tight">${displayTitle}</p>
            </div>
        </div>
        <div class="flex flex-col items-end justify-center ml-3">
            <span class="font-bold ${amountColor} text-[16px] tracking-tight leading-none">${amountPrefix}${formatCurrency(tx.amount)}</span>
            ${runningBalanceHtml}
        </div>
    `;
    item.addEventListener('click', () => { openEditModal(tx); });
    dom.transactionList.appendChild(item);
}

async function handleTransactionSubmit(e) {
    e.preventDefault();
    const type = dom.txType.value; 
    const category = dom.txCategory.value;
    const description = dom.txDescription.value.trim();
    let amount = parseFloat(dom.txAmount.value);
    const date = dom.txDate.value; 

    if (isNaN(amount) || amount < 0 || !date || amount > MAX_AMOUNT) {
        validateTxForm(true); // Show errors
        return;
    }
    // 移除 Math.round，保留兩位小數
    amount = parseFloat(amount.toFixed(2));
    
    const txData = { type, description, amount, date, category: category || 'cat_general' };
    try {
        if (state.currentEditingId) {
            await storageService.updateTransaction(state.currentEditingId, txData);
            showMessage(t('updateSuccess'));
        } else {
            txData.user_id = state.userId;
            await storageService.addTransaction(txData);
            showMessage(t('createSuccess'));
        }
        await loadTransactions();
        closeAddTxModal(); 
        window.dispatchEvent(new CustomEvent('app:transactionUpdated'));
        // 在 UI 操作完成後的背景觸發同步
        setTimeout(() => syncService.run(), 500);
    } catch (error) {
        console.error("Save failed:", error);
        closeAddTxModal(); 
    }
}

async function deleteTransaction(localId) {
    try {
        await storageService.deleteTransaction(localId);
        showMessage(t('deleteSuccess'));
        await loadTransactions();
        window.dispatchEvent(new CustomEvent('app:transactionUpdated'));
        closeAddTxModal(); 
        setTimeout(() => syncService.run(), 500);
    } catch (error) {
        console.error("Delete failed:", error);
        closeAddTxModal(); 
    }
}

function filterCategories(type) {
    let firstVisible = null;
    dom.txCategoryOptionItems.forEach(option => {
        if (option.dataset.type === type) {
            option.classList.remove('hidden');
            option.style.setProperty('display', 'flex', 'important');
            if (!firstVisible) firstVisible = option.dataset.value;
        } else {
            option.classList.add('hidden');
            option.style.setProperty('display', 'none', 'important');
        }
    });
    return firstVisible;
}

function validateTxForm(showErrors = true) {
    const amountVal = dom.txAmount.value;
    const amount = parseFloat(amountVal);
    let isAmountValid = (amountVal !== '' && !isNaN(amount) && amount >= 0 && amount <= MAX_AMOUNT);

    if (showErrors) {
        if (dom.amountError) {
            const errText = isAmountValid ? '' : (amount > MAX_AMOUNT ? t('amountTooLarge') : t('amountInvalid'));
            dom.amountError.textContent = errText;
            if (errText) {
                dom.amountError.classList.remove('hidden');
            } else {
                dom.amountError.classList.add('hidden');
            }
        }
        if (!isAmountValid) {
            dom.txAmount.classList.add('border-red-500');
            dom.txAmount.classList.remove('border-slate-200/80', 'dark:border-white/10');
        } else {
            dom.txAmount.classList.remove('border-red-500');
            dom.txAmount.classList.add('border-slate-200/80', 'dark:border-white/10');
        }
    } else {
        if (dom.amountError) {
            dom.amountError.textContent = '';
            dom.amountError.classList.add('hidden');
        }
        dom.txAmount.classList.remove('border-red-500');
        dom.txAmount.classList.add('border-slate-200/80', 'dark:border-white/10');
    }
    
    if (isAmountValid) {
        dom.saveTxBtn.classList.remove('opacity-50', 'cursor-not-allowed');
    } else {
        dom.saveTxBtn.classList.add('opacity-50', 'cursor-not-allowed');
    }
}

function resetTxModal() {
    dom.txModalTitle.textContent = t('newTransaction');
    dom.txDescription.value = '';
    dom.txAmount.value = '';
    state.setCurrentEditingId(null);
    
    updateTypeToggleUI('expense');
    
    dom.txCategoryOptions.classList.add('hidden');
    dom.deleteTxBtnModal.style.display = 'none';

    if (dom.addTxModalContent) {
        dom.addTxModalContent.style.transform = 'translateY(0)';
        dom.addTxModalContent.style.maxHeight = '';
    }
    const modalBody = document.getElementById('add-tx-modal-body');
    if (modalBody) {
        modalBody.scrollTop = 0;
    }
    
    // 同步類別選單的高亮狀態
    refreshCategoryOptionsUI();
    
    // Fix: 每次打開新增視窗時，強制更新 Flatpickr 的 maxDate 為「此時此刻」
    // 這是為了防止 App 在背景執行跨日後，maxDate 仍停留在舊日期導致無法選擇今天的問題
    if (state.flatpickrInstance) {
        const today = new Date();
        state.flatpickrInstance.set('maxDate', today.setHours(23, 59, 59, 999));
        state.flatpickrInstance.setDate(new Date(), false); // 重設為今天，不觸發 onChange 以避免不必要的邏輯
    }

    // 模式切換：新增模式下取消按鈕伸長 (100% 寬度)
    const btnContainer = document.getElementById('modal-footer-btns');
    if (btnContainer) {
        btnContainer.classList.remove('grid-cols-2');
        btnContainer.classList.add('grid-cols-1');
    }

    if (dom.charCounter) dom.charCounter.textContent = '0 / 50';
    validateTxForm(false); 
}

function openEditModal(tx) {
    dom.txModalTitle.textContent = t('editTransaction');
    state.setCurrentEditingId(tx.id);
    
    updateTypeToggleUI(tx.type); 
    
    dom.txCategory.value = tx.category || 'cat_general'; 
    dom.txCategoryBtnText.textContent = t(tx.category || 'cat_general');
    
    dom.txDescription.value = tx.description;
    dom.txAmount.value = tx.amount;
    validateTxForm(); // Validate immediately with populated data
    dom.deleteTxBtnModal.style.display = 'block';

    // 模式切換：編輯模式下取消與刪除平分寬度 (50/50)
    const btnContainer = document.getElementById('modal-footer-btns');
    if (btnContainer) {
        btnContainer.classList.remove('grid-cols-1');
        btnContainer.classList.add('grid-cols-2');
    }

    if (dom.charCounter) dom.charCounter.textContent = `${tx.description.length} / 50`;
    dom.addTxModal.style.display = 'flex';
    
    // 同步類別選單的高亮狀態
    refreshCategoryOptionsUI();
    
    setTimeout(async () => {
        const dp = state.flatpickrInstance;
        if (dp) {
            // Fix: 編輯模式也要更新 maxDate，防止跨日後無法選擇今天
            const today = new Date();
            dp.set('maxDate', today.setHours(23, 59, 59, 999));
            
            dp.setDate(tx.date, false); 
        }

        // 延遲聚焦金額輸入框 (50ms 夠用且感覺瞬間)
        if (dom.txAmount) {
            dom.txAmount.focus({ preventScroll: true });
            // 對於 type="number"，重新賦值可以確保光標在末尾 (在某些瀏覽器行為中)
            const val = dom.txAmount.value;
            dom.txAmount.value = '';
            dom.txAmount.value = val;
            setTimeout(() => {
                if (document.activeElement === dom.txAmount) {
                    dom.txAmount.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 200);
        }
    }, 50);
}

function getLocalTodayStr() {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export async function initializeDatePicker(defaultDateStr = "today") {
    const selector = "#tx-date-picker";
    let targetInput = document.querySelector(selector);
    
    // 1. 銷毀舊實例
    if (state.flatpickrInstance) {
        state.flatpickrInstance.destroy();
        state.setFlatpickrInstance(null);
    }

    const todayStr = getLocalTodayStr();

    // 2. 物理替換節點 (解決切換語言失效的終極方案)
    if (targetInput && targetInput.parentElement) {
        const parent = targetInput.parentElement;
        const newInput = document.createElement('input');
        newInput.id = 'tx-date-picker';
        newInput.type = 'text';
        newInput.className = "w-full px-3 py-2 bg-slate-100/80 dark:bg-slate-800/50 rounded-xl text-slate-800 dark:text-white font-medium outline-none text-sm border border-slate-200/80 dark:border-white/10 focus:border-indigo-500 focus:ring-0 transition-colors duration-150";
        newInput.autocomplete = "off";
        parent.replaceChild(newInput, targetInput);
        targetInput = newInput;
        dom.txDate = newInput;
    }

    if (!targetInput) return;

    const locale = await getFlatpickrLocale(currentLang);
    if (locale && locale.months) {
        locale.months.longhand = locale.months.shorthand;
    }

    const maxDateObj = new Date();
    maxDateObj.setHours(23, 59, 59, 999);

    const dp = flatpickr(targetInput, {
        dateFormat: "Y-m-d",
        defaultDate: defaultDateStr === "today" ? todayStr : defaultDateStr,
        locale: locale, 
        maxDate: maxDateObj,
        altInput: false,
        disableMobile: true,
        monthSelectorType: "static", 
        static: true, // 嵌入式：日曆直接在 input 下方 DOM 流中，跟隨 modal 滾動移動，零位置偏差
        onOpen: (selectedDates, dateStr, instance) => {
            // 打開日曆時收起軟鍵盤
            if (document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
            }

            // 開啟日曆時讓抽屜彈窗向上伸展，不超過頂部劉海兒，讓一螢幕能完整顯示日曆
            const modalContent = document.getElementById('add-tx-modal-content');
            if (modalContent) {
                modalContent.style.height = 'calc(100vh - env(safe-area-inset-top, 0px) - 64px)';
            }

            setTimeout(() => {
                if (instance.calendarContainer) {
                    instance.calendarContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }, 60);
        },
        onClose: (selectedDates, dateStr, instance) => {
            // 日曆收起時恢復抽屜彈窗原本高度
            const modalContent = document.getElementById('add-tx-modal-content');
            if (modalContent) {
                modalContent.style.height = '';
            }
        },
        onReady: (sd, ds, inst) => {
            if (state.currentThemeIsDark && inst.calendarContainer) {
                inst.calendarContainer.classList.add('dark');
            }
            if (inst.input) {
                inst.input.placeholder = t('selectDate');
            }
        }
    });
    const instance = Array.isArray(dp) ? dp[0] : dp;
    state.setFlatpickrInstance(instance);

    // 再次點擊已開啟的日期輸入框時收起日曆，並透過 capture phase 阻斷 Flatpickr 重新開啟
    let wasOpenOnPointerDown = false;

    targetInput.addEventListener('pointerdown', () => {
        wasOpenOnPointerDown = !!(instance && instance.isOpen);
    }, true);

    targetInput.addEventListener('click', (e) => {
        if (wasOpenOnPointerDown) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (instance && instance.isOpen) {
                instance.close();
            }
            targetInput.blur();
            wasOpenOnPointerDown = false;
        }
    }, true);

    targetInput.addEventListener('focus', (e) => {
        if (wasOpenOnPointerDown) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            targetInput.blur();
        }
    }, true);
}

export function initializeTransactions() {
    initializeDatePicker();
    refreshCategoryOptionsUI();

    // 監聽交易更新或偏好變更事件以重新載入與繪製交易列表
    window.addEventListener('app:transactionUpdated', () => {
        loadTransactions();
    });

    // 監聽語言變更事件以重新初始化日期選擇器與類別圖示
    window.addEventListener('app:languageChanged', () => {
        initializeDatePicker();
        refreshCategoryOptionsUI();
    });

    if (dom.transactionForm) {
        dom.transactionForm.addEventListener('submit', handleTransactionSubmit);
    }
    if (dom.saveTxBtn) {
        dom.saveTxBtn.addEventListener('click', handleTransactionSubmit);
    }
    
    // Simple background click handler
    dom.addTxModal.addEventListener('mousedown', (e) => {
        if (e.target === dom.addTxModal || e.target.id === 'add-tx-mask' || e.target.id === 'add-tx-modal-scroll') {
            closeAddTxModal();
        }
    });
    
    dom.openAddModalBtn.onclick = (e) => { 
        e.preventDefault();
        openAddTransactionModal();
    };

    dom.closeAddModal.addEventListener('click', (e) => { e.preventDefault(); closeAddTxModal(); });
    dom.cancelAddBtn.addEventListener('click', (e) => { e.preventDefault(); closeAddTxModal(); });
    dom.deleteTxBtnModal.addEventListener('click', () => {
        if (state.currentEditingId) showConfirm(t('deleteConfirm'), () => deleteTransaction(state.currentEditingId));
    });

    dom.typeExpenseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        updateTypeToggleUI('expense');
    });

    dom.typeIncomeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        updateTypeToggleUI('income');
    });

    dom.txCategoryBtn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation(); 
        filterCategories(dom.txType.value || 'expense');
        dom.txCategoryOptions.classList.toggle('hidden');
    });
    
    dom.txCategoryOptionItems.forEach(option => {
        option.addEventListener('click', (e) => {
            e.preventDefault(); e.stopPropagation(); 
            const value = option.dataset.value;
            console.log('Category selected:', value);
            dom.txCategory.value = value; 
            dom.txCategoryBtnText.textContent = t(value); 
            
            // 強制刷新選單 UI (含高亮)
            refreshCategoryOptionsUI();

            // 強制關閉選單
            dom.txCategoryOptions.classList.add('hidden');
        });
    });

    dom.txDescription.addEventListener('input', () => {
        if (dom.charCounter) dom.charCounter.textContent = `${dom.txDescription.value.length} / 50`;
        validateTxForm();
    });
    dom.txAmount.addEventListener('input', validateTxForm);

    // 導出數據
    if (dom.exportDataBtn) {
        dom.exportDataBtn.addEventListener('click', (e) => {
            e.preventDefault();
            startDataExport();
        });
    }
}